import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { CollectionInvoiceDocument, CollectionInvoicePdf } from '../model';
import { formatOptionalDate } from '../utils/format';
import { apiAbsoluteUrl } from '../utils/pdf';
import { useInvoicePdfUpload } from '../hooks/useInvoicePdfUpload';

interface Props {
  open: boolean;
  onClose: () => void;
  company: string;
  documents: CollectionInvoiceDocument[];
  location: string;
  acctNo: string;
  onInvoicePdfUploaded?: (applyTo: string, pdf: CollectionInvoicePdf, acctNo: string) => void;
}

const InvoicePdfsModal: React.FC<Props> = ({
  open,
  onClose,
  company,
  documents,
  location,
  acctNo,
  onInvoicePdfUploaded,
}) => {
  const { fileInputRef, triggerUpload, handleFileSelected, uploadingApplyTo, canUpload } = useInvoicePdfUpload({
    location,
    acctNo,
    onUploaded: (applyTo, pdf) => onInvoicePdfUploaded?.(applyTo, pdf, acctNo),
  });
  const matched = documents.filter((d) => d.has_pdf).length;
  const total = documents.length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <input ref={fileInputRef} type="file" accept="application/pdf,.pdf" hidden onChange={handleFileSelected} />
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6">Invoices — {company}</Typography>
          <Typography variant="body2" color="textSecondary">
            {matched} of {total} {total === 1 ? 'invoice has a PDF' : 'invoices have PDFs'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: 0 }}>
        {documents.length === 0 ? (
          <Box sx={{ p: 3, color: 'text.secondary' }}>
            <Typography variant="body2">No past-due invoices.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Invoice #</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Due</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>PDF</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {documents.map((doc) => (
                  <TableRow key={doc.apply_to} hover>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{doc.apply_to}</TableCell>
                    <TableCell>{formatOptionalDate(doc.invoice_date)}</TableCell>
                    <TableCell>{formatOptionalDate(doc.due_date)}</TableCell>
                    <TableCell align="right">${doc.amount.toFixed(2)}</TableCell>
                    <TableCell>
                      {doc.pdf ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PictureAsPdfIcon sx={{ color: '#e53935', fontSize: 18, flexShrink: 0 }} />
                          <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                            {doc.pdf.filename}
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="body2" color="textSecondary">
                            No PDF available
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<UploadFileIcon />}
                            disabled={!canUpload || uploadingApplyTo === doc.apply_to}
                            onClick={() => triggerUpload(doc.apply_to)}
                          >
                            {uploadingApplyTo === doc.apply_to ? 'Uploading…' : 'Upload PDF'}
                          </Button>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {doc.pdf?.download_url ? (
                        <Button
                          component="a"
                          size="small"
                          variant="outlined"
                          startIcon={<DownloadIcon />}
                          href={apiAbsoluteUrl(doc.pdf.download_url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          download={doc.pdf.filename}
                        >
                          Download
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InvoicePdfsModal;
